import { Box, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { type TenediCarrierIdentityFieldsFragment } from "../../../generated/graphql";
import { CARRIER_FIELD_COPY } from "../tenedi-connection-copy";
import {
  CARRIER_ISA_FIELDS,
  CARRIER_SCAC_FIELDS,
  carrierFormFromEntity,
  carrierFormSchema,
  type CarrierFormValues,
  formErrors,
} from "../tenedi-connection-forms";
import IdentityCardLayout, { identityCardStyles } from "./identity-card-layout";
import IdentityTextField from "./identity-text-field";

type CarrierIdentityCardProps = {
  readonly carrier: TenediCarrierIdentityFieldsFragment;
  readonly saveDisabled: boolean;
  /** Saves the card; a rejection's message shows on the card. */
  readonly onSave: (values: CarrierFormValues) => Promise<void>;
};

/**
 * The carrier's EDI identity: ISA id and four alternates, SCAC and four
 * alternates, Stedi partner id. "Save carrier IDs" writes all of them in one
 * call. The page remounts the card after each save, so it starts from what
 * the server stored.
 */
const CarrierIdentityCard = ({
  carrier,
  saveDisabled,
  onSave,
}: CarrierIdentityCardProps) => {
  const [values, setValues] = useState<CarrierFormValues>(() =>
    carrierFormFromEntity(carrier),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const handleFieldChange = useCallback(
    (name: keyof CarrierFormValues, value: string) => {
      setValues((previous) => ({ ...previous, [name]: value }));
    },
    [],
  );

  const handleSave = useCallback(() => {
    const found = formErrors(carrierFormSchema, values);
    setErrors(found);
    setSaveError(null);
    if (Object.keys(found).length > 0) {
      return;
    }
    setSaving(true);
    onSave(values)
      .catch((error: unknown) => {
        setSaveError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        setSaving(false);
      });
  }, [onSave, values]);

  const renderField = (name: keyof CarrierFormValues) => (
    <IdentityTextField
      key={name}
      copy={CARRIER_FIELD_COPY[name]}
      disabled={saving}
      error={errors[name]}
      name={name}
      value={values[name]}
      onChange={handleFieldChange}
    />
  );

  return (
    <IdentityCardLayout
      formError={errors.form}
      saveDisabled={saveDisabled}
      saveError={saveError}
      saveLabel="Save carrier IDs"
      saving={saving}
      subtitle="The carrier receives tenders on these IDs and sends 990, 214 and 210 messages from them. Each ID belongs to one carrier only."
      title={`Carrier identity: ${carrier.name}`}
      onSave={handleSave}
    >
      <Typography sx={identityCardStyles.section} variant="subtitle2">
        EDI IDs
      </Typography>
      <Box sx={identityCardStyles.grid}>
        {CARRIER_ISA_FIELDS.map((name) => renderField(name))}
      </Box>
      <Typography sx={identityCardStyles.section} variant="subtitle2">
        SCACs
      </Typography>
      <Box sx={identityCardStyles.grid}>
        {CARRIER_SCAC_FIELDS.map((name) => renderField(name))}
      </Box>
      <Box sx={identityCardStyles.grid}>{renderField("stediPartnerId")}</Box>
    </IdentityCardLayout>
  );
};

export default CarrierIdentityCard;
